import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Check, ShieldCheck } from 'lucide-react';

import { IconTile } from '@/design-system/patterns/IconTile';
import { Alert } from '@/design-system/ui/Alert';
import { Button } from '@/design-system/ui/Button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@/design-system/ui/Dialog';
import { LEGAL_CHANGES, LEGAL_HIGHLIGHTS, LEGAL_VERSION, needsTermsAcceptance } from '@escala/domain/legal';
import { useStore } from '@/store/AppStore';
import { PRIVACY_ACCOUNT_KEY } from '@/store/privacy';

import { useLegal } from './context';

/**
 * Novo aceite dos Termos e da Política: logo depois do login, quando a versão aceita não
 * é a atual (ou a conta nunca aceitou). Mostra o que mudou; não fecha sem uma escolha —
 * "Aceitar e continuar" ou sair.
 */
export default function TermsUpdateDialog({ onLogout }: { onLogout: () => void }) {
  const { me, org, privacy } = useStore();
  const { openLegal } = useLegal();
  const queryClient = useQueryClient();
  const key = [...PRIVACY_ACCOUNT_KEY, me.id];
  const account = useQuery({ queryKey: key, queryFn: privacy.account });
  const accept = useMutation({
    mutationFn: privacy.acceptTerms,
    onSuccess: (fresh) => queryClient.setQueryData(key, fresh),
  });

  if (!account.data || !needsTermsAcceptance(account.data.termsVersion)) return null;

  const previous = account.data.termsVersion;
  // Primeiro aceite: o essencial dos documentos; versão nova: o que mudou.
  const points = previous && LEGAL_CHANGES.length ? LEGAL_CHANGES : LEGAL_HIGHLIGHTS;

  return (
    <Dialog open>
      <DialogContent
        role="alertdialog"
        described
        className="w-[min(520px,calc(100vw-24px))] gap-4 p-6"
        onEscapeKeyDown={(e) => e.preventDefault()}
        onPointerDownOutside={(e) => e.preventDefault()}
        onInteractOutside={(e) => e.preventDefault()}
      >
        <IconTile size="lg">
          <ShieldCheck aria-hidden />
        </IconTile>
        <div className="flex flex-col gap-1">
          <DialogTitle className="m-0 text-title-sm font-extrabold tracking-[-0.02em]">
            {previous
              ? 'Atualizamos nossa Política de Privacidade'
              : 'Termos de Uso e Política de Privacidade'}
          </DialogTitle>
          <DialogDescription className="m-0 text-small text-pretty text-ink-secondary">
            {previous
              ? `A versão ${LEGAL_VERSION} substitui a ${previous}. Para continuar usando o app, confira o que mudou:`
              : 'Antes de continuar, confira como a igreja e a Live Gospel cuidam dos seus dados:'}
          </DialogDescription>
        </div>
        <ul className="m-0 flex list-none flex-col gap-2 p-0">
          {points.map((change) => (
            <li key={change} className="flex items-start gap-2 text-small text-pretty text-ink-secondary">
              <Check aria-hidden className="mt-0.5 size-4 shrink-0 stroke-2 text-success" />
              {change}
            </li>
          ))}
        </ul>
        {accept.isError ? (
          <Alert tone="danger">Não foi possível registrar o aceite agora. Tente de novo.</Alert>
        ) : null}
        <div className="flex flex-wrap items-center justify-end gap-2">
          <Button variant="ghost" className="mr-auto" onClick={onLogout}>
            Sair
          </Button>
          <Button variant="secondary" onClick={() => openLegal('privacy', { orgName: org.name })}>
            Ler política completa
          </Button>
          <Button onClick={() => accept.mutate()} disabled={accept.isPending}>
            Aceitar e continuar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
